<template>
  <div
      class="window-container"
      :class="{ max: max.state }"
      :style="{ left: pos.x + 'px', top: pos.y + 'px', padding: border }"
      @click.capture="$emit('active')"
  >
    <div v-show="!max.state">
      <div
          class="resize-border top"
          @mousedown="onMouseDown('resizeTop', $event)"
          :style="{ width: pos.w + 'px', left: border, height: border }"
      ></div>
      <div
          class="resize-border bottom"
          @mousedown="onMouseDown('resizeBottom', $event)"
          :style="{ width: pos.w + 'px', left: border, height: border }"
      ></div>
      <div
          class="resize-border left"
          @mousedown="onMouseDown('resizeLeft', $event)"
          :style="{ height: pos.h + 'px', top: border, width: border }"
      ></div>
      <div
          class="resize-border right"
          @mousedown="onMouseDown('resizeRight', $event)"
          :style="{ height: pos.h + 'px', top: border, width: border }"
      ></div>
    </div>

    <div class="box" :style="{ width: pos.w + 'px', height: pos.h + 'px' }">
      <div class="header" @mousedown="onMouseDown('move', $event)">
        <div class="title">{{ windowDatum.title }}</div>
        <div class="btn-group">
          <sc-icon
              class="btn"
              type="LineOutlined"
              @click="$emit('minimize')"
          />
          <sc-icon
              class="btn"
              type="ExpandOutlined"
              @click="maximize"
              v-show="!max.state"
          />
          <sc-icon
              class="btn"
              type="CompressOutlined"
              @click="normalize"
              v-show="max.state"
          />
          <sc-icon class="btn" type="CloseOutlined" @click="$emit('close')"/>
        </div>
      </div>
      <a-config-provider :locale="local">
      <div class="body" :style="{padding: windowDatum.padding + 'px'}">
        <router-view
            v-if="windowDatum.content.type === 'route'"
            :key="router.currentRoute.path"
        />
        <component
            v-else
            :is="windowDatum.content.component"
            v-bind="windowDatum.content.props"
        />
      </div>
      </a-config-provider>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType, provide} from 'vue';
import {FakeApp} from './FakeApp';
import {WindowDatum, WindowTool} from './WindowInterface';
import {createMemoryRouter} from '@/router';
import zhCN from "ant-design-vue/es/locale/zh_CN";

export default defineComponent({
  name: 'Window',
  props: {
    windowDatum: {
      type: Object as PropType<WindowDatum>,
      required: true,
    },
  },
  data() {
    return {
      local: zhCN,
      offset: {
        action: '',
        x: 0,
        y: 0,
        h: 0,
        w: 0,
      },
      pos: {
        x: 0,
        y: 0,
        h: 0,
        w: 0,
      },
      max: {
        state: false,
        x: 0,
        y: 0,
        h: 0,
        w: 0,
      },
      clickTime: 0,
    };
  },

  computed: {
    border(): string {
      return (this.max.state ? 0 : 3) + 'px';
    },
  },
  setup(prop, context) {
    const app = new FakeApp();
    const router = createMemoryRouter();
    app.use(router);
    for (const provider of app.providers) {
      provide(provider.key, provider.value);
    }
    provide(WindowTool, {
      setReturnData: (data: any) => {
        context.emit('setReturnData', data);
      },
      close: (data: any) => {
        context.emit('close', data);
      },
    });
    return {
      router,
      app,
    };
  },

  mounted() {
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
    this.parseData(this.windowDatum);
  },

  beforeUnmount() {
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    this.app.unmount();
  },

  methods: {
    zhCN() {
      return zhCN
    },
    parseData(data: WindowDatum) {
      this.pos.y = data.top;
      this.pos.x = data.left;
      this.pos.h = data.height;
      this.pos.w = data.width;
      if (data.content.type === 'route') {
        this.router.push(data.content.href);
      }
    },
    onMouseDown(
        action:
            | 'resizeTop'
            | 'resizeLeft'
            | 'resizeBottom'
            | 'resizeRight'
            | 'move',
        event: MouseEvent
    ) {
      if (action === 'move') {
        const preClickTime = this.clickTime;
        this.clickTime = Date.now();
        if (this.clickTime - preClickTime < 300) {
          if (this.max.state) {
            this.normalize();
          } else {
            this.maximize();
          }
        }
      }
      if (this.max.state) {
        return false;
      }
      event.preventDefault();
      this.offset.action = action;
      this.$emit('active');
      this.$nextTick(() => {
        this[action].call(this, event);
      });
      return false;
    },

    resizeTop(event: MouseEvent) {
      this.offset.y = this.pos.y - event.clientY;
      this.offset.h = this.pos.h + event.clientY;
    },

    resizeBottom(event: MouseEvent) {
      this.offset.h = this.pos.h - event.clientY;
    },

    resizeLeft(event: MouseEvent) {
      this.offset.x = this.pos.x - event.clientX;
      this.offset.w = this.pos.w + event.clientX;
    },

    resizeRight(event: MouseEvent) {
      this.offset.w = this.pos.w - event.clientX;
    },

    move(event: MouseEvent) {
      this.offset.x = this.pos.x - event.clientX;
      this.offset.y = this.pos.y - event.clientY;
    },

    maximize() {
      this.max.x = this.pos.x;
      this.max.y = this.pos.y;
      this.max.h = this.pos.h;
      this.max.w = this.pos.w;
      this.max.state = true;

      this.pos.x = 0;
      this.pos.y = 0;
      this.pos.h = document.documentElement.clientHeight;
      this.pos.w = document.documentElement.clientWidth;
    },

    normalize() {
      this.pos.x = this.max.x;
      this.pos.y = this.max.y;
      this.pos.h = this.max.h;
      this.pos.w = this.max.w;
      this.max.state = false;
    },

    onMouseMove(event: MouseEvent) {
      let top = this.pos.y;
      let left = this.pos.x;
      let width = this.pos.w;
      let height = this.pos.h;
      const boundary = {
        top: -3,
        bottom: 60,
        left: 80,
        right: 80,
      };

      switch (this.offset.action) {
        case 'move':
          left = event.clientX + this.offset.x;
          left = Math.max(boundary.left - width, left);
          left = Math.min(document.body.clientWidth - boundary.right, left);

          top = event.clientY + this.offset.y;
          top = Math.max(boundary.top, top);
          top = Math.min(document.body.clientHeight - boundary.bottom, top);
          break;
        case 'resizeTop':
          top = event.clientY + this.offset.y;
          height = this.offset.h - event.clientY;
          if (top < -3) {
            top = -3;
            height = this.offset.h + this.offset.y - top;
          }
          if (top > document.body.clientHeight - 90) {
            top = document.body.clientHeight - 90;
            height = this.offset.h + this.offset.y - top;
          }
          if (height < 200) {
            height = 200;
            top = this.offset.h - height + this.offset.y;
          }
          break;
        case 'resizeBottom':
          height = this.offset.h + event.clientY;
          if (height < 200) {
            height = 200;
          }
          break;
        case 'resizeLeft':
          left = event.clientX + this.offset.x;
          width = this.offset.w - event.clientX;
          if (left < 150 - width) {
            left = 150 - width;
            width = this.offset.w + this.offset.x - left;
          }
          if (left > document.body.clientWidth - 150) {
            left = document.body.clientWidth - 150;
            width = this.offset.w + this.offset.x - left;
          }
          if (width < 200) {
            width = 200;
            left = this.offset.w - width + this.offset.x;
          }
          break;
        case 'resizeRight':
          width = this.offset.w + event.clientX;
          if (width < 200) {
            width = 200;
          }
          break;
      }
      this.pos.x = left;
      this.pos.y = top;
      this.pos.h = height;
      this.pos.w = width;
      return false;
    },

    onMouseUp() {
      this.offset.action = '';
      return false;
    },
  },
});
</script>

<style lang="scss" scoped>
$resize-width: 3px;
$title-height: 42px;

.window-container {
  position: absolute;
  z-index: $index-middle;

  &.max .header {
    border-radius: 0 !important;
  }

  .resize-border {
    position: absolute;

    &.top,
    &.bottom {
      cursor: n-resize;
      left: $resize-width;
      height: $resize-width;
    }

    &.left,
    &.right {
      cursor: ew-resize;
      top: $resize-width;
      width: $resize-width;
    }

    &.top {
      top: 0;
    }

    &.bottom {
      bottom: 0;
    }

    &.left {
      left: 0;
    }

    &.right {
      right: 0;
    }
  }

  > .box {
    background-color: transparent;
    box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.2);
    cursor: auto;
    display: flex;
    flex-direction: column;

    > .header {
      user-select: none;
      display: flex;
      flex-direction: row;
      justify-content: center;
      flex-shrink: 0;
      color: $color-content-text;
      background-color: $color-content-bg-secondary;
      height: $title-height;
      padding-left: $spacing-large;
      cursor: move;
      border-radius: $border-radius-medium $border-radius-medium 0 0;
      overflow: hidden;

      > .title {
        flex-grow: 1;
        font-size: $font-size;
        word-break: break-word;
        line-height: $title-height;
      }

      > .btn-group {
        flex-shrink: 0;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;

        > .btn {
          padding: $spacing-small;
          line-height: $title-height;
          height: $title-height;
          margin-left: $spacing-small;
          font-size: $font-size;
          cursor: pointer;
          box-sizing: border-box;
          @include flexRowCenter();

          &:last-child {
            padding-right: $spacing-small * 2;
          }

          &:hover {
            background-color: darken($color-content-bg-primary, 3);
          }
        }
      }
    }

    > .body {
      background-color: $color-content-bg;
      flex-grow: 1;
      position: relative;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
  }
}
</style>
